import { useMemo, useState } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { t } from 'utilities/TranslationUtils';
import { observer } from 'mobx-react-lite';
import Check from '@mui/icons-material/Check';

// components
import { AppSectionErrorBoundary, AppSpacer, AppDialog } from 'components/ui';
import { AppButton } from 'components/form';
import { StepIconProps } from '@mui/material/StepIcon';
import SelectUserStep from './components/SelectUserStep';
import SelectOrganizationStep from './components/SelectOrganizationStep';
import InviteUserStep from './components/InviteUserStep';

// types
import { SingleUserType } from 'types/GlobalTypes';
import { useStores } from 'index';
import { UserData } from 'types/models/UserData';
import { UserDataAndOrganization } from 'types/models/OrganizationModels';

type PropsType = {
  organizationId: string;
  isOpened: boolean;
  user: SingleUserType | null;
  onClose: () => void;
};

export const AddUserDialog = observer(({ organizationId, isOpened, user, onClose }: PropsType) => {
  const { organizationsStore, usersStore, inviteStore, roleStore, notificationStore, apiServiceAxiosInstance: apiInstance } = useStores();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserDataAndOrganization | null>(null);
  const [isInvitingUser, setIsInvitingUser] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [startingRole, setStartingRole] = useState<string | null>(null);
  const trackManEmailHandle = "@trackman.com";
  
  const steps = useMemo(
    () => [
      { label: t('usersPage.selectUser'), id: 'selectUser' },
      { label: t('usersPage.selectOrganization'), id: 'selectOrganization' },
      { label: t('usersPage.inviteUser'), id: 'inviteUser' },
    ],
    []
  );

  const onCloseHandler = () => {
    onClose();
    setTimeout(() => {
      setActiveStep(0);
      setSelectedUser(null);
      setIsInvitingUser(false);
      setIsAddingUser(false);
    }, 500);
  };
  
  const CanBeAddedDirectly = () => {
    let userEmail = selectedUser?.email;
    if (!selectedUser?.id || !userEmail || !userEmail.toLowerCase().endsWith(trackManEmailHandle)) {
      return false;
    }
    return true;
  }
  const getOrganization = (orgId: string) => {
    const organization = organizationsStore.organizations.find((org) => org.id === orgId);

    return organization || null;
  };

  const onSelectUserHandler = (user: UserData) => {
    if (!user) return;
    let startingRoleName = user.roleName ?? "Admin";
    
    setSelectedUser({ ...user, roleName: startingRoleName, organization: user.organizationId ? getOrganization(user.organizationId) : null });
    setStartingRole(startingRoleName);
    setActiveStep(1);
  };

  const onChangeUserRoleHandler = (roleName: string) => {
    if (!selectedUser || !roleName) return;
    
    setSelectedUser({ ...selectedUser, roleName: roleName });
  };

  // =========================================================?

  const onInviteUserHandler = async () => {
    if (!selectedUser || !selectedUser.roleName) return;
    let roleId = roleStore.roles.find((role) => role.name === selectedUser.roleName)?.id
    setIsInvitingUser(true);
    const response = await inviteStore.sendInvite(apiInstance, {
      email: selectedUser.email,
      roleId: roleId as string,
      roleName: selectedUser.roleName,
      organizationId: organizationId,
      firstName: selectedUser.firstName,
      lastName: selectedUser.lastName
    })

    if (response) {
      usersStore.getUsers(apiInstance, organizationId);
      onCloseHandler();
    } else {
      console.log('error');
    }

    setIsInvitingUser(false);
  };

  const onAddUserDirectlyHandler = async () => {
    if (!selectedUser || !selectedUser.roleName) return;
    if (startingRole != selectedUser.roleName){
      await notificationStore.addToast(
        'Error',
        "Cannot change the role of a user during direct assignment",
        'info'
      );
      return;
    }
    setIsAddingUser(true);
    
    const response = await usersStore.ReassignTrackmanUser(apiInstance, {
      organizationId: organizationId,
      roleName: selectedUser.roleName,
      userId: selectedUser.id,
    });

    if (response) {
      usersStore.getUsers(apiInstance, organizationId);
      onCloseHandler();
    } else {
      console.log('error');
    }

    setIsAddingUser(false);
  };
  return (
    <AppDialog
      classNames={`user-dialog child-align-items-start child-m-h-700px overflow-auto`}
      title={`${t('usersPage.addOrInviteUser')}`}
      isOpened={isOpened}
      onClose={() => onCloseHandler()}
    >
      <div className='flex-auto d-flex flex-column justify-content-between'>
        <AppSectionErrorBoundary>
          <div>
            <section className='container'>
              <Stepper alternativeLabel activeStep={activeStep}>
                {steps.map(({ label, id }, index) => {
                  const stepProps: { completed?: boolean } = {};

                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel StepIconComponent={StepIconComponent}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </section>
            <AppSpacer space='20px' />
            <section className='container'>
              {activeStep === 0 && <SelectUserStep selectedUser={selectedUser} onSelectUser={(user) => onSelectUserHandler(user)} />}
              {activeStep === 1 && (
                <SelectOrganizationStep
                  selectedUser={selectedUser}
                  organization={getOrganization(organizationId)}
                  onChangeUserRole={(role) => onChangeUserRoleHandler(role)}
                  onClickNext={() => setActiveStep((prevActiveStep) => prevActiveStep + 1)}
                />
              )}
              {activeStep === 2 && (
                <InviteUserStep
                  onInviteUser={() => onInviteUserHandler()}
                  onAddDirectly={() => onAddUserDirectlyHandler()}
                  isInvitingUser={isInvitingUser}
                  isAddingUser={isAddingUser}
                  isAddingDirectlyDisabled={!CanBeAddedDirectly()}
                />
              )}
            </section>
            <AppSpacer space='20px' />
          </div>
          <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
            <div>
              <AppButton variant='secondary' onClick={() => onCloseHandler()} data-test='user-dialog-cancel-button'>
                {t('common.cancel')}
              </AppButton>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <AppButton
                variant='secondary'
                disabled={activeStep === 0}
                onClick={() => {
                  if (activeStep === 0) return;
                  setActiveStep((prevActiveStep) => prevActiveStep + -1);
                }}
                data-test='user-dialog-cancel-button'
              >
                {t('common.back')}
              </AppButton>
            </div>
          </footer>
        </AppSectionErrorBoundary>
      </div>
    </AppDialog>
  );
});

const StepIconComponent = (props: StepIconProps) => {
  const { active, completed, className } = props;

  return (
    <div className={`add-user-dialog-stepper ${active ? 'active' : ''} ${completed ? 'completed' : ''} ${className}`}>
      {completed ? <Check className='completedIcon' /> : <div className='circle' />}
    </div>
  );
};
